import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useTheme } from '../../theme/ThemeContext';

/**
 * GradientButton - A reusable button component with two variants:
 * 
 * - "primary" (default): Lit-up borders style - solid inner background that becomes 
 *   transparent on hover to reveal the gradient border (Hero button style)
 * 
 * - "filled": Solid gradient fill that darkens on hover (Contact button style)
 */
const GradientButton = forwardRef(({
    children,
    onClick,
    startIcon,
    endIcon,
    variant = 'primary',
    fullWidth = false,
    type = 'button',
    disabled = false,
    sx = {},
    ...props
}, ref) => {
    const { mode } = useTheme();

    // Design system gradients
    const gradients = {
        light: {
            main: 'linear-gradient(to right, #0984E3, #00B894)',
            hover: 'linear-gradient(to right, #0770C0, #009B7B)',
        },
        dark: {
            main: 'linear-gradient(to right, #74B9FF, #55EFC4)',
            hover: 'linear-gradient(to right, #5AA3E8, #3DD9A8)',
        },
    };

    const currentGradient = gradients[mode];

    // Primary variant: Lit-up borders (Hero style)
    if (variant === 'primary') {
        return (
            <Box
                ref={ref}
                component="button"
                type={type}
                onClick={onClick}
                disabled={disabled}
                sx={{
                    width: fullWidth ? '100%' : 'auto',
                    p: '3px',
                    position: 'relative',
                    background: 'transparent',
                    border: 'none',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    borderRadius: '10px',
                    opacity: disabled ? 0.6 : 1,
                    '&:focus': {
                        outline: 'none',
                    },
                    '&:hover:not(:disabled) .button-inner': {
                        background: 'transparent',
                    },
                    ...sx,
                }}
                {...props}
            >
                {/* Gradient border layer */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: currentGradient.main,
                        borderRadius: '10px',
                    }}
                />
                {/* Inner button content */}
                <Box
                    className="button-inner"
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        px: { xs: 3, sm: 4 },
                        py: { xs: 1, sm: 1.5 },
                        background: mode === 'light' ? '#ffffff' : '#2D3436',
                        borderRadius: '8px',
                        transition: 'background 0.2s ease',
                    }}
                >
                    {startIcon && (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: mode === 'light' ? '#2D3436' : '#ffffff' }}>
                            {startIcon}
                        </Box>
                    )}
                    <Typography
                        sx={{
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            fontWeight: 500,
                            color: mode === 'light' ? '#2D3436' : '#ffffff',
                        }}
                    >
                        {children}
                    </Typography>
                    {endIcon && (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: mode === 'light' ? '#2D3436' : '#ffffff' }}>
                            {endIcon}
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    // Filled variant: Solid gradient (Contact style)
    return (
        <Box
            ref={ref}
            component="button"
            type={type}
            onClick={onClick}
            disabled={disabled}
            sx={{
                width: fullWidth ? '100%' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: { xs: 3, sm: 4 },
                py: { xs: 1, sm: 1.5 },
                position: 'relative',
                background: currentGradient.main,
                border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                borderRadius: '10px',
                opacity: disabled ? 0.6 : 1,
                transition: 'all 0.2s ease',
                '&:focus': {
                    outline: 'none',
                },
                '&:hover:not(:disabled)': {
                    transform: 'translateY(-2px)',
                    background: currentGradient.hover,
                },
                ...sx,
            }}
            {...props}
        >
            {startIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', color: '#ffffff' }}>
                    {startIcon}
                </Box>
            )}
            <Typography
                sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: 500,
                    color: '#ffffff',
                }}
            >
                {children}
            </Typography>
            {endIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', color: '#ffffff' }}>
                    {endIcon}
                </Box>
            )}
        </Box>
    );
});

GradientButton.displayName = 'GradientButton';

GradientButton.propTypes = {
    /** Button content/label */
    children: PropTypes.node.isRequired,
    /** Click handler */
    onClick: PropTypes.func,
    /** Icon to display before the label */
    startIcon: PropTypes.node,
    /** Icon to display after the label */
    endIcon: PropTypes.node,
    /** Button variant: 'primary' (lit-up borders) or 'filled' (solid gradient) */
    variant: PropTypes.oneOf(['primary', 'filled']),
    /** Whether button takes full width of container */
    fullWidth: PropTypes.bool,
    /** HTML button type */
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    /** Whether button is disabled */
    disabled: PropTypes.bool,
    /** Additional MUI sx props */
    sx: PropTypes.object,
};

export default GradientButton;
